import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ModeratorTestCard, payoutSelectTriggerClass, type ModeratorTestItem } from "@/components/get-paid";
import type { useGetPaid } from "../useGetPaid";
import { useLanguages } from "@/hooks/use-languages";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { TestSubmissionRecycleBinDialog } from "../components/TestSubmissionRecycleBinDialog";
import type { TestSubmissionTrashItem } from "@/api/tests.api";

type Progress = { sent: boolean; signed: boolean; finalValidated: boolean };
type Ctx = ReturnType<typeof useGetPaid>;

interface ModeratorTestListProps {
  t: Ctx["t"];
  tests: ModeratorTestItem[];
  isPending: boolean;
  emptyLabel: string;
  moderationLang: string;
  setModerationLang: (value: string) => void;
  loadingTests: boolean;
  onRefresh: () => void;
  contractProgressByTest: Record<string, Progress>;
  onDetails: (test: ModeratorTestItem) => void;
  onSendContract: (test: ModeratorTestItem) => void;
  onFinalValidate: (test: ModeratorTestItem) => void;
  sendingContractTestId: string | null;
  finalValidatingTestId: string | null;
  isSuperAdmin: boolean;
  trashItems: TestSubmissionTrashItem[];
  trashLoading: boolean;
  trashError: boolean;
  trashHasMore: boolean;
  trashLoadingMore: boolean;
  onLoadMoreTrash: () => void;
  onTrash: (test: ModeratorTestItem, reason: string) => Promise<boolean>;
  onRestore: (id: string) => Promise<void>;
}

const NO_PROGRESS: Progress = { sent: false, signed: false, finalValidated: false };

/** Tests of one step, filtered by language, with a refresh. */
export function ModeratorTestList(props: ModeratorTestListProps) {
  const { t, tests, isPending, emptyLabel, moderationLang, setModerationLang, loadingTests, onRefresh } = props;
  const { activeLanguages } = useLanguages({ scope: "all" });
  const languageOptions = useMemo(
    () => activeLanguages.map((lang) => ({ value: lang.code, label: lang.displayName || lang.name || lang.code })),
    [activeLanguages],
  );

  return (
    <section className="min-w-0 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Select value={moderationLang} onValueChange={setModerationLang}>
          <SelectTrigger aria-label={t.selectLanguagePrompt} className={cn(payoutSelectTriggerClass, "w-full sm:w-[220px]")}>
            <SelectValue placeholder={t.selectLanguagePrompt} />
          </SelectTrigger>
          <SelectContent>
            {languageOptions.map((lang) => (
              <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap items-center gap-2">
          {props.isSuperAdmin ? (
            <TestSubmissionRecycleBinDialog
              t={t}
              items={props.trashItems}
              isLoading={props.trashLoading}
              hasError={props.trashError}
              hasMore={props.trashHasMore}
              isLoadingMore={props.trashLoadingMore}
              onLoadMore={props.onLoadMoreTrash}
              onRestore={props.onRestore}
            />
          ) : null}
          <Button type="button" variant="outline" size="sm" disabled={loadingTests} onClick={onRefresh}>
            <Icon name="refreshCw" className={cn(loadingTests && "animate-spin")} />
            {loadingTests ? t.getPaid.loading : t.getPaid.refresh}
          </Button>
        </div>
      </div>

      {tests.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/60 px-4 py-10 text-center text-sm text-muted-foreground">{emptyLabel}</div>
      ) : (
        <div>
          {tests.map((test) => (
            <ModeratorTestCard
              key={test.id}
              test={test}
              t={t}
              isPending={isPending}
              contractProgress={props.contractProgressByTest[String(test.id)] || NO_PROGRESS}
              onDetails={props.onDetails}
              onSendContract={props.onSendContract}
              onFinalValidate={props.onFinalValidate}
              sendingContractTestId={props.sendingContractTestId}
              finalValidatingTestId={props.finalValidatingTestId}
              isSuperAdmin={props.isSuperAdmin}
              onTrash={props.onTrash}
            />
          ))}
        </div>
      )}
    </section>
  );
}
